import {
  collection,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  setDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { getFirestoreDb } from '../config';
import { deleteRoomImage, uploadRoomImage } from '../storage';
import {
  DEFAULT_ROOM_SCENE_ID,
  type CreateRoomInput,
  type PersistentRoom,
  type RoomMember,
  type RoomSceneId,
  type UpdateRoomInput,
} from '../../../types/room';
import { ROOMS_COLLECTION } from './constants';
import { convertMember, generateRoomId } from './utils';
import { logWarning } from '../../logging/logger';

/**
 * Creates a new persistent room.
 * The room ID is used as the LiveKit room name.
 */
export async function createPersistentRoom(
  input: CreateRoomInput,
  user: { uid: string; email: string; displayName: string }
): Promise<PersistentRoom> {
  const db = getFirestoreDb();
  const roomId = generateRoomId();

  let imageUrl: string | null = null;
  if (input.imageFile) {
    try {
      imageUrl = await uploadRoomImage(roomId, input.imageFile);
    } catch (error) {
      logWarning('Failed to upload room image, continuing without image.', error);
    }
  }

  const member: RoomMember = {
    userId: user.uid,
    email: user.email,
    role: 'admin',
    joinedAt: new Date(),
  };

  const roomData = {
    id: roomId,
    name: input.name,
    imageUrl,
    sceneId: resolveSceneId(input.sceneId),
    dailyRoomName: roomId, // Used by VR app for LiveKit room name
    createdBy: user.uid,
    createdAt: serverTimestamp(),
    members: [member],
    adminUserIds: [user.uid],
  };

  await setDoc(doc(db, ROOMS_COLLECTION, roomId), roomData);

  return {
    ...roomData,
    createdAt: new Date(),
  } as PersistentRoom;
}

/**
 * Gets a persistent room by ID.
 */
export async function getPersistentRoom(roomId: string): Promise<PersistentRoom | null> {
  const db = getFirestoreDb();
  const docRef = doc(db, ROOMS_COLLECTION, roomId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    ...data,
    sceneId: resolveSceneId(data.sceneId),
    members: (data.members || []).map(convertMember),
    createdAt: data.createdAt?.toDate() || new Date(),
  } as PersistentRoom;
}

/**
 * Gets all persistent rooms for a user (as member or admin).
 * Matches by userId or email to support invited users.
 */
export async function getUserPersistentRooms(
  userId: string,
  email?: string
): Promise<PersistentRoom[]> {
  const db = getFirestoreDb();
  const roomsRef = collection(db, ROOMS_COLLECTION);
  const snapshot = await getDocs(roomsRef);

  const rooms: PersistentRoom[] = [];

  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    const members = data.members || [];
    const isMember = members.some(
      (member: { userId: string; email?: string }) =>
        member.userId === userId || (email && member.email === email)
    );

    if (isMember) {
      rooms.push({
        ...data,
        sceneId: resolveSceneId(data.sceneId),
        members: members.map(convertMember),
        createdAt: data.createdAt?.toDate() || new Date(),
      } as PersistentRoom);
    }
  });

  return rooms;
}

/**
 * Updates a persistent room.
 */
export async function updatePersistentRoom(
  roomId: string,
  input: UpdateRoomInput
): Promise<void> {
  const db = getFirestoreDb();
  const docRef = doc(db, ROOMS_COLLECTION, roomId);

  const updates: { name?: string; imageUrl?: string; sceneId?: RoomSceneId } = {};

  if (input.name !== undefined) {
    updates.name = input.name;
  }

  if (input.sceneId !== undefined) {
    updates.sceneId = resolveSceneId(input.sceneId);
  }

  if (input.imageFile) {
    updates.imageUrl = await uploadRoomImage(roomId, input.imageFile);
  }

  if (Object.keys(updates).length > 0) {
    await updateDoc(docRef, updates);
  }
}

function resolveSceneId(sceneId: unknown): RoomSceneId {
  void sceneId;
  // Single-theme rollout: force Star Lounge regardless of stored value.
  return DEFAULT_ROOM_SCENE_ID;
}

/**
 * Deletes a persistent room.
 */
export async function deletePersistentRoom(roomId: string): Promise<void> {
  const db = getFirestoreDb();
  await deleteRoomImage(roomId);
  await deleteDoc(doc(db, ROOMS_COLLECTION, roomId));
}
